import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkReload'

// A single passive background refetch (react-query fires one on reconnect — laptop
// wake / wifi blip — because staleTime is 0) used to surface as a hard error and strand
// the user on react-admin's "Something Went Wrong" screen, which only resets on a route
// change. This boundary makes that state recoverable in place: it resets the cached
// query errors and re-renders, with a one-shot auto-retry for transient blips plus
// manual "Try again" / "Reload" affordances — so the page heals without a full reload.

// Module-level guard so a persistently failing render can't auto-retry in a tight loop.
// A fresh error that occurs after the cooldown is allowed one automatic attempt.
let lastAutoRetryAt = 0
const AUTO_RETRY_COOLDOWN_MS = 20000
const AUTO_RETRY_DELAY_MS = 2500

interface RecoverableErrorProps {
  error?: Error | null
  onRetry: () => void
}

export const RecoverableError = ({ error, onRetry }: RecoverableErrorProps) => {
  useEffect(() => {
    const now = Date.now()
    // Only auto-retry if we haven't recently — if a prior auto-retry just failed and we
    // landed back here, fall through to the manual buttons instead of looping.
    if (now - lastAutoRetryAt <= AUTO_RETRY_COOLDOWN_MS) return
    lastAutoRetryAt = now
    const timer = setTimeout(onRetry, AUTO_RETRY_DELAY_MS)
    return () => clearTimeout(timer)
  }, [onRetry])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        minHeight: 320,
        height: '100%',
        p: 4,
        textAlign: 'center',
      }}
      role="alert"
    >
      <Typography variant="h6">Something went wrong</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
        We hit a problem loading this page. This is usually temporary — retrying often fixes it.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
        <Button variant="contained" onClick={onRetry}>
          Try again
        </Button>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Box>
      {import.meta.env.DEV && error?.message && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, wordBreak: 'break-word' }}>
          {error.message}
        </Typography>
      )}
    </Box>
  )
}

const BoundaryFallback = ({ error, onReset }: { error: Error | null; onReset: () => void }) => {
  const queryClient = useQueryClient()
  const handleRetry = useCallback(() => {
    // Drop cached query errors so the children refetch fresh on remount instead of
    // re-rendering the same failed result and immediately re-tripping the boundary.
    queryClient.resetQueries()
    onReset()
  }, [queryClient, onReset])

  return <RecoverableError error={error} onRetry={handleRetry} />
}

interface AppErrorBoundaryProps {
  children: React.ReactNode
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, { error: Error | null }> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A React.lazy route chunk that 404s after a deploy throws here (it does not surface as
    // an unhandledrejection). Reload once to fetch the new build; guarded so it can't loop.
    // If a reload is triggered the page navigates away immediately; otherwise the recoverable
    // fallback below is shown.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return
    // Surface for diagnostics (App.tsx also has global error listeners).
    console.error('[AppErrorBoundary]', error, info?.componentStack)
  }

  handleReset = () => this.setState({ error: null })

  render() {
    if (this.state.error) {
      return <BoundaryFallback error={this.state.error} onReset={this.handleReset} />
    }
    return this.props.children
  }
}

export default AppErrorBoundary
